"use client";

// Client component: the constellation's shots are timed by script (random skill, irregular intervals). Everything else
// is in the server-rendered HTML, so the twelve skills read without JavaScript.

import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import Button from "@/components/ds/button";
import SectionHeading from "@/components/ds/section-heading";
import Reveal from "@/components/motion/reveal";
import { HABIT_MARK, type Brand } from "./brand";
import { SECTION_IDS } from "./sections";
import { TOUR, createTour, cursorPath, placeTip, tipFor, type PathPoint } from "./skills-tour";
import "./skills.css";

/* Where a skill sits around the North star, above 900 px: side -1 left, 1 right, 0 straight above or below the star;
   t from 0 (next to the rings) to 1 (the edge of the column); y in px from the top of the 500 px map. Listed
   clockwise from the top. */
type Place = { side: -1 | 0 | 1; t: number; y: number };

// Icons: the 24 px strokes of the previous skill cloud, drawn at 16 px in currentColor.
const SKILLS: { label: string; place: Place; icon: ReactNode }[] = [
  {
    label: "Closing binder",
    place: { side: 0, t: 0, y: 60 },
    icon: (
      <>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </>
    ),
  },
  {
    label: "Signature packet",
    place: { side: 1, t: 0.2, y: 105 },
    icon: (
      <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </>
    ),
  },
  {
    label: "Title review",
    place: { side: 1, t: 0.7, y: 180 },
    icon: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="m9 15 2 2 4-4" />
      </>
    ),
  },
  {
    label: "NDA review",
    place: { side: 1, t: 1, y: 255 },
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
  {
    label: "Entity formation",
    place: { side: 1, t: 0.8, y: 330 },
    icon: (
      <>
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <path d="M9 22v-4h6v4" />
        <path d="M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01" />
      </>
    ),
  },
  {
    label: "Deadline calendar",
    place: { side: 1, t: 0.25, y: 405 },
    icon: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </>
    ),
  },
  {
    label: "Client letter",
    place: { side: 0, t: 0, y: 440 },
    icon: (
      <>
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-10 5L2 7" />
      </>
    ),
  },
  {
    label: "Diligence index",
    place: { side: -1, t: 0.25, y: 395 },
    icon: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  },
  {
    label: "Cap table",
    place: { side: -1, t: 0.8, y: 320 },
    icon: (
      <>
        <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
        <path d="M22 12A10 10 0 0 0 12 2v10z" />
      </>
    ),
  },
  {
    label: "Intake",
    place: { side: -1, t: 1, y: 245 },
    icon: (
      <>
        <path d="M22 12h-6l-2 3h-4l-2-3H2" />
        <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
      </>
    ),
  },
  {
    label: "Conflict check",
    place: { side: -1, t: 0.7, y: 170 },
    icon: (
      <>
        <circle cx="18" cy="18" r="3" />
        <circle cx="6" cy="6" r="3" />
        <path d="M13 6h3a2 2 0 0 1 2 2v7M11 18H8a2 2 0 0 1-2-2V9" />
      </>
    ),
  },
  {
    label: "Matter brief",
    place: { side: -1, t: 0.15, y: 95 },
    icon: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
      </>
    ),
  },
];

const place = ({ side, t, y }: Place, i: number) => {
  const drift = DRIFTS[i % DRIFTS.length] ?? DRIFTS[0]!;
  return {
    "--side": side,
    "--t": t,
    "--y": `${y}px`,
    "--i": 5 + (i % 2),
    // How this one wanders (planDrift below): two amplitudes, two periods, two phases. Read by skills.css.
    "--dr": `${drift.r}px`,
    "--dt": `${drift.t}px`,
    "--dz": drift.z,
    "--wander": `${drift.wander}s`,
    "--wander-at": `${drift.wanderAt}s`,
    "--depth": `${drift.depth}s`,
    "--depth-at": `${drift.depthAt}s`,
  } as CSSProperties;
};

/* ---------- shots: pure, so a seeded generator makes them repeatable in tests ---------- */

/** A number in [0, 1), like Math.random. */
export type Random = () => number;

/** Seeded generator (mulberry32): the same seed gives the same shots. */
export function seededRandom(seed: number): Random {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---------- drift: how each chip wanders, drawn once from a seeded generator ---------- */

/** How one chip wanders. Amplitudes in px along its own ray (r, towards and away from the star) and across it (t,
    either way round); z is a share of its size, the depth. Then two periods and two phases: `wander` carries r and t,
    `depth` carries z, and they are never the same number, so a chip's state repeats only every lcm of the two — never
    while anyone is looking.
    Both were doubled on 19/09 (Jérémy: "I do not get the feeling it is more alive by default"). The v6 draw was 2 to
    4.4 px on a 19 to 33 s turn: about a pixel and a half in the three seconds anyone actually spends looking at the
    block — true motion, invisible motion. It is now 5 to 10 px on a 9 to 16 s turn, so a chip covers 4 to 7 px in
    those three seconds, and the whole constellation sways over it (skills.css, v2-skills-sway). The depth stays
    deliberately small, 0.6 to 1.4 %: it is the one axis that resamples the label, and blurring a name to prove the
    block is alive would be a bad trade. */
export type Drift = { r: number; t: number; z: number; wander: number; wanderAt: number; depth: number; depthAt: number };

const round = (value: number, digits: number) => Number(value.toFixed(digits));

/** One draw for `count` chips. Seeded (mulberry32, as the shots are), so the page, the tests and the boards all read
    the same twelve wanderings. */
export function planDrift(random: Random, count: number): Drift[] {
  return Array.from({ length: count }, () => {
    const wander = round(9 + random() * 7, 3); // 9 to 16 s for one turn
    const depth = round(6 + random() * 5, 3); // 6 to 11 s for one breath
    return {
      r: round(5 + random() * 5, 2), // 5 to 10 px along the ray
      t: round((3.5 + random() * 4) * (random() < 0.5 ? -1 : 1), 2), // 3.5 to 7.5 px across it, either way round
      z: round(0.006 + random() * 0.008, 4), // 0.6 to 1.4 % of its size
      wander,
      wanderAt: round(-random() * wander, 3),
      depth,
      depthAt: round(-random() * depth, 3),
    };
  });
}

/** The twelve wanderings of this constellation. */
export const DRIFTS = planDrift(seededRandom(0x5c111), SKILLS.length);

export const SHOTS = {
  /** From the map coming on screen, or from the end of the chips' reveal, to the first volley, in ms. */
  first: 700,
  /** From one volley to the next, in ms. */
  wait: [900, 2200],
  /** Share of volleys that send a second ball almost at once, and how long after the first. */
  doubleChance: 0.25,
  doubleGap: [90, 260],
} as const;

const between = (random: Random, [min, max]: readonly [number, number]) => Math.round(min + random() * (max - min));

/** A skill drawn at random among the ones not in `avoid` (all of them if that leaves none). */
export function pickSkill(random: Random, count: number, avoid: readonly number[]) {
  const options = Array.from({ length: count }, (_, i) => i).filter((i) => !avoid.includes(i));
  const pool = options.length ? options : Array.from({ length: count }, (_, i) => i);
  return pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))] ?? 0;
}

export type Volley = { shots: { index: number; at: number }[]; wait: number };

/** The next volley after the ball sent to `last`: one ball (sometimes two, to two other skills), never the same skill
    twice in a row, then an irregular wait. */
export function planVolley(random: Random, count: number, last: number | null): Volley {
  const first = pickSkill(random, count, last === null ? [] : [last]);
  const shots = [{ index: first, at: 0 }];
  if (count > 2 && random() < SHOTS.doubleChance) {
    shots.push({ index: pickSkill(random, count, [first, ...(last === null ? [] : [last])]), at: between(random, SHOTS.doubleGap) });
  }
  return { shots, wait: between(random, SHOTS.wait) };
}

/** Flight time of a ball, in ms: longer rays take a little longer, with some jitter. */
export function flightTime(random: Random, distance: number) {
  return Math.round(380 + distance * 0.8 + (random() - 0.5) * 140);
}

/** Time left, in ms, before the reveals (components/motion: the v2-reveal animation) among these animations have played:
    0 once they are over, or with none. A reveal not started yet counts whole. */
export function revealLeft(animations: readonly Animation[]) {
  let left = 0;
  for (const animation of animations) {
    if (!("animationName" in animation) || animation.animationName !== "v2-reveal") continue;
    const timing = animation.effect?.getComputedTiming();
    left = Math.max(left, Number(timing?.endTime ?? 0) - Number(timing?.localTime ?? 0));
  }
  return left;
}

export type VolleyHost = {
  count: number;
  /** Ms before the chips have all revealed; 0 once they have. */
  reveal(): number;
  /** True while the tour runs (Jérémy, 23/09: "suspendre les volées pendant une visite"): no automatic ball leaves,
      so the one the cursor sends is the only one in the air. A ball already flying finishes its flight. */
  held(): boolean;
  /** The skill the last ball went to, by whatever path. */
  last(): number | null;
  fire: (index: number) => void;
};

/** The automatic volleys' clock. Its timers are its own: stop() drops every one of them (a press on the star, the map
    going idle), and start() books the next volley. Held, a volley is skipped and the next one looked for a fresh wait
    away, so the volleys come back at most SHOTS.wait[1] after the tour stops. */
export function createVolleys(host: VolleyHost, random: Random = Math.random) {
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number) => {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
  };
  const volley = () => {
    // The chips are still revealing: the volley goes SHOTS.first after they have all landed.
    const reveal = host.reveal();
    if (reveal > 0) return later(volley, reveal + SHOTS.first);
    if (host.held()) return later(volley, between(random, SHOTS.wait));
    const plan = planVolley(random, host.count, host.last());
    for (const shot of plan.shots) {
      if (shot.at) later(() => host.fire(shot.index), shot.at);
      else host.fire(shot.index);
    }
    later(volley, plan.wait);
  };
  return {
    start(delay: number) {
      later(volley, delay);
    },
    stop() {
      for (const id of timers) clearTimeout(id);
      timers.clear();
    },
  };
}

/* ---------- the constellation ---------- */

// The North mark (public/assets/v2/top/north-mark-nav.svg) at the centre of the constellation, drawn solid in the
// matter of this block's own call to action, "Browse the library" (components/ds/button.css, variant dark). Nested
// layers, each owning one property: the outer <span> breathes with the glass disc and the <svg> bounces when a ball
// leaves. The gradient plate under the clip path stays still since 23/09 (it used to slide with each volley); only
// the bounce answers a ball now (skills.css).
// viewBox: the drawing is 32 x 32.2371 and its area centroid sits 0.3526 below the middle of that box (five polygons,
// shoelace formula), so the box is shifted by exactly that much — the mark is centred on its mass, not on its
// bounding box, which is what "centred" means for a shape this asymmetric.
// `brand` (brand.ts): on the /logo-test preview the clip is the Habit mark instead — six shapes drawn in a 70 x 70
// square, brought to this drawing's 32.2371 units by the clip's own transform, so the plate, its gradient and every
// rule of skills.css are the ones the North mark has. Symmetric on both axes: its box is not shifted.
function NorthMark({ brand }: { brand: Brand }) {
  const habit = brand === "habit";
  return (
    <span className="v2-skills__mark" aria-hidden="true">
      <svg className="v2-skills__mark-shape" viewBox={habit ? "0 0 32.2371 32.2371" : "-0.1208 0.3526 32.2371 32.2371"} fill="none">
        <defs>
          {/* The five polygons of the mark, as the clip the gradient plate is cut by. They sit straight in the
              clipPath: a <use> pointing at a group is not a valid child of one, and Chrome draws nothing. */}
          {habit ? (
            <clipPath id="v2-skills-mark-clip" transform={`scale(${32.2371 / 70})`}>
              {HABIT_MARK.map((d) => (
                <path key={d} d={d} />
              ))}
            </clipPath>
          ) : (
            <clipPath id="v2-skills-mark-clip">
              <path d="M14.6568 0L9.01412 14.6696L14.1037 13.1866L16.9481 17.8685L18.7365 11.8687L20.6359 11.2035L14.6568 0Z" />
              <path d="M32 7.30201L20.314 12.9891L18.4146 20.3764L32 7.30201Z" />
              <path d="M12.1679 15.0718L0 18.5939L8.92839 21.4718L12.1284 15.0718H12.1679Z" />
              <path d="M13.6003 16.1186L6.01509 30.7226L17.1911 21.1816L13.6003 16.1186Z" />
              <path d="M21.6991 19.0505L16.6818 23.6036L27.0325 32.2371L21.7386 19.0076L21.6991 19.0505Z" />
            </clipPath>
          )}
          {/* Diagonal like the page's night gradient; the two stops are coloured by skills.css. The plate is three
              times the drawing, so the mark shows the middle third of the gradient. It no longer slides (23/09):
              the size is what let it slide without running out of matter, and is harmless now that it stays put. */}
          <linearGradient id="v2-skills-mark-ink" x1="0" y1="0" x2="1" y2="1">
            <stop className="v2-skills__mark-stop v2-skills__mark-stop--rest" offset="0.3" />
            <stop className="v2-skills__mark-stop v2-skills__mark-stop--lit" offset="0.7" />
          </linearGradient>
        </defs>
        <g clipPath="url(#v2-skills-mark-clip)">
          <rect className="v2-skills__mark-plate" x="-32.36" y="-31.89" width="96.71" height="96.71" fill="url(#v2-skills-mark-ink)" />
        </g>
      </svg>
    </span>
  );
}

/* Runs the shots while the map is at least 30 % on screen and the tab is shown, and sets data-motion on the map:
   "live" (sonar, pulse and shots play), "idle" (everything paused where it is), absent under reduced motion (nothing
   moves; skills.css only animates under data-motion). A shot restarts the CSS animations of a ray, a chip and the mark
   by switching their data-shot / data-hit between "a" and "b", which replaces the chip's reveal: so no ball, automatic or
   on hover, goes to a chip before the block is in view and the chip's reveal has played (up to ~1.2 s), and the first
   volley waits for the end of all the reveals. The draws use Math.random; the tests seed the pure functions above
   instead. */
function useShots() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const map = ref.current;
    if (!map) return;
    const rays = [...map.querySelectorAll<HTMLElement>(".v2-skills__ray")];
    const chips = [...map.querySelectorAll<HTMLElement>(".v2-skills__chip")];
    const hub = map.querySelector<HTMLElement>(".v2-skills__hub");
    const mark = map.querySelector<HTMLElement>(".v2-skills__mark");
    const core = map.querySelector<HTMLElement>(".v2-skills__core");
    const button = map.querySelector<HTMLButtonElement>(".v2-skills__press");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const constellation = window.matchMedia("(min-width: 901px)");
    // Balls start at the edge of the glass disc (skills.css).
    const launch = parseFloat(getComputedStyle(map).getPropertyValue("--launch")) || 0;
    let visible = false;
    let live = false;
    let last: number | null = null;

    const flip = (el: HTMLElement, key: "shot" | "hit" | "press") => {
      el.dataset[key] = el.dataset[key] === "a" ? "b" : "a";
    };
    // Still hidden or rising: a shot would cut the reveal short (the chip would jump to full opacity and move).
    const revealing = (chip: HTMLElement) => !map.closest("[data-inview]") || revealLeft(chip.getAnimations()) > 0;

    const fire = (index: number) => {
      const chip = chips[index];
      const ray = rays[index];
      if (!chip || revealing(chip)) return;
      last = index;
      let flight = 0;
      if (constellation.matches && ray && hub) {
        // From the star to where the ray meets the chip's edge (the chip is centred on the end of its ray).
        const dx = chip.offsetLeft - hub.offsetLeft;
        const dy = chip.offsetTop - hub.offsetTop;
        const length = Math.hypot(dx, dy) || 1;
        const edge = Math.min(
          dx ? chip.offsetWidth / 2 / (Math.abs(dx) / length) : Infinity,
          dy ? chip.offsetHeight / 2 / (Math.abs(dy) / length) : Infinity,
        );
        const reach = Math.max(launch, length - edge + 2);
        flight = flightTime(Math.random, reach - launch);
        ray.style.setProperty("--reach", `${reach.toFixed(1)}px`);
        ray.style.setProperty("--reach-scale", ((reach - launch) / Math.max(1, length - launch)).toFixed(3));
        ray.style.setProperty("--flight", `${flight}ms`);
        flip(ray, "shot");
      }
      chip.style.setProperty("--flight", `${flight}ms`);
      flip(chip, "hit");
      // The star answers the ball it just sent: a short bounce (its gradient plate no longer slides, since 23/09).
      if (mark) flip(mark, "shot");
    };

    // Held while the tour runs (below): the cursor's ball is then the only one. `tour` is read when a volley is due,
    // long after it exists.
    const volleys = createVolleys({
      count: chips.length,
      reveal: () => revealLeft(chips.flatMap((chip) => chip.getAnimations())),
      held: () => tour.running,
      last: () => last,
      fire,
    });

    /* The tour (skills-tour.ts): a simulated cursor visits every skill in a shuffled order, the skill answers exactly
       as it does under a real mouse (the same fire(), with the same guard as onOver), and a tooltip reads it out.
       Only while the map is live, as a constellation (above 900 px), on a device with a real pointer, and never
       under reduced motion. A real mouse on the map does not stop it (Jérémy, 23/09: "même si j'ai le curseur sur
       l'animation"); a real mouse on a CHIP wins: that chip's tooltip opens at once, the simulated cursor hides and
       its visit ends without a tooltip (tour.hold), and the round goes on when the mouse leaves the chip
       (tour.release). The tour keeps its own timers, so a press on the star (which drops the volleys' timers) leaves
       it alone. While it runs, the automatic volleys are held (createVolleys). */
    const cursor = map.querySelector<HTMLElement>(".v2-skills__cursor");
    const tip = map.querySelector<HTMLElement>(".v2-skills__tip");
    const disc = map.querySelector<HTMLElement>(".v2-skills__press");
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    let hovered: number | null = null;
    /* The cursor's travel of the visit under way (skills-tour.ts, cursorPath), in px in the map, and its playing. The
       Web Animations API, not a CSS transition: an arc and a bounce are a path of many points, not one easing. */
    let path: PathPoint[] = [];
    let travel: Animation | null = null;
    /* Fills the tooltip with a skill and places it clear of the eleven other chips and the glass disc, measured now,
       relative to the map. The tour calls it once per visit, a real hover once per chip. */
    const seat = (index: number) => {
      const box = chips[index]?.querySelector<HTMLElement>(".v2-skills__chip-box");
      const label = SKILLS[index]?.label;
      const text = label ? tipFor(label) : undefined;
      if (!box || !tip || !label || !text) return null;
      const m = map.getBoundingClientRect();
      const r = box.getBoundingClientRect();
      const chip = { x: r.left - m.left, y: r.top - m.top, w: r.width, h: r.height };
      const [name, blurb, meta] = [".v2-skills__tip-name", ".v2-skills__tip-blurb", ".v2-skills__tip-meta"].map((s) =>
        tip.querySelector<HTMLElement>(s),
      );
      if (name) name.textContent = label;
      if (blurb) blurb.textContent = text.blurb;
      if (meta) meta.textContent = `${text.domain} · ${text.jurisdiction}`;
      const avoid = [...chips.filter((_, i) => i !== index).map((c) => c.querySelector(".v2-skills__chip-box")), disc].flatMap((el) => {
        const b = el?.getBoundingClientRect();
        return b ? [{ x: b.left - m.left, y: b.top - m.top, w: b.width, h: b.height }] : [];
      });
      const at = placeTip(chip, { w: tip.offsetWidth, h: tip.offsetHeight }, { w: m.width, h: m.height }, avoid);
      tip.style.setProperty("--tx", `${at.x}px`);
      tip.style.setProperty("--ty", `${at.y}px`);
      tip.dataset.side = at.side;
      tip.dataset.skill = label;
      return chip;
    };
    const tour = createTour({
      count: chips.length,
      // Not in view yet: look again shortly. Reveals still playing (the star's comes after the chips'): the first
      // visit goes TOUR.first after the last one has landed, like the first volley.
      wait: () => {
        if (!map.closest("[data-inview]")) return 300;
        const left = revealLeft(map.getAnimations({ subtree: true }));
        return left > 0 ? left + TOUR.first : 0;
      },
      show: (index, visit) => {
        // Measured once, at the start of the visit: the chip may drift a few px meanwhile.
        const chip = cursor ? seat(index) : null;
        if (!cursor || !chip) return false;
        // The arrow's tip (1 px into its drawing) lands a little right of and below the middle of the pill.
        const x = chip.x + chip.w * 0.6 - 1;
        const y = chip.y + chip.h * 0.6 - 1;
        cursor.style.setProperty("--cx", `${x.toFixed(1)}px`);
        cursor.style.setProperty("--cy", `${y.toFixed(1)}px`);
        cursor.style.setProperty("--cdx", `${visit.dx}px`);
        cursor.style.setProperty("--cdy", `${visit.dy}px`);
        path = cursorPath(visit).map((p) => ({ ...p, x: x + p.x, y: y + p.y }));
        return true;
      },
      stage: (stage) => {
        // A new visit: the last travel, if a real mouse cut it short, never drags the cursor from where it was.
        if (stage === "at") travel?.cancel();
        map.dataset.tour = stage;
        // On: it travels onto the chip and bounces (glide + settle). The CSS rests it on the chip underneath, with no
        // transition on transform, so the two never fight; the travel ends where the CSS already is.
        if (stage === "on" && cursor && !reduce.matches) {
          travel = cursor.animate(
            path.map(({ x, y, offset }) => ({ offset, transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)` })),
            { duration: TOUR.glide + TOUR.settle, easing: "linear" },
          );
        }
      },
      arrive: (index) => {
        if (index !== last) fire(index);
      },
    });
    const steer = (delay: number) => {
      if (live && constellation.matches && fine.matches && !reduce.matches) tour.start(delay);
      else tour.stop();
    };
    const onMedia = () => steer(TOUR.first);

    const update = () => {
      const on = visible && !document.hidden && !reduce.matches;
      if (reduce.matches) delete map.dataset.motion;
      else map.dataset.motion = on ? "live" : "idle";
      if (on !== live) {
        live = on;
        if (on) volleys.start(SHOTS.first);
        else volleys.stop();
      }
      steer(TOUR.first);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = !!entry?.isIntersecting;
        update();
      },
      { threshold: 0.3 },
    );
    io.observe(map);

    /* A mouse over a skill sends it a ball (unless the last one just went there, or the skill is still revealing: the
       ball the cursor just landed with is the only one), and opens that skill's tooltip at once, in place of the
       tour's (skills.css, data-hover): one tooltip, never two. Leaving the chip hands the tooltip back to the tour. */
    const onOver = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || !live) return;
      const chip = (event.target as Element).closest<HTMLElement>(".v2-skills__chip");
      const index = chip ? chips.indexOf(chip) : -1;
      if (index < 0 || index === hovered) return;
      if (index !== last) fire(index);
      tour.hold();
      if (!seat(index)) return;
      hovered = index;
      map.dataset.hover = "on";
    };
    const unhover = () => {
      if (hovered === null) return;
      hovered = null;
      delete map.dataset.hover;
      tour.release();
    };
    const onOut = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || hovered === null) return;
      // Still inside the same chip, or onto another one (onOver takes it from there).
      const to = event.relatedTarget instanceof Element ? event.relatedTarget.closest(".v2-skills__chip") : null;
      if (!to) unhover();
    };

    /* The star is a push button. A press sends one ball to a skill drawn at random (the same fire() the automatic
       volleys use), and the automatic rhythm starts over: every pending timer is dropped and the next volley is booked
       a fresh wait away, so a press is never followed half a beat later by a volley that was already in the post.
       TEN PRESSES IN A ROW COST ONE ANIMATION, NOT TEN. Everything the press plays is CSS restarted by flipping one
       attribute between "a" and "b" (data-press on the core, exactly as data-shot works on a ray): a second press
       replaces the first course from its first frame instead of stacking a second Animation object on the element.
       That is the merge, rather than a queue: a queue would make the tenth press answer a second later, which is not
       what a buzzer does.
       It answers the PRESS, not the release: pointerdown for a finger or a mouse, keydown for Enter and Space (Space
       scrolls the page otherwise, hence preventDefault; a held key repeats, hence event.repeat). `click` is kept as
       the way in for anything that activates a button without either — assistive technology, a voice command — and
       only when no press has just been handled. Under reduced motion the signal still leaves (the attributes still
       flip); skills.css simply has no animation to play, because data-motion is absent. */
    let pressedAt = -Infinity;
    const press = () => {
      pressedAt = performance.now();
      if (core) flip(core, "press");
      // The same draw as a volley, and the same guard: never the skill the last ball went to. With twelve skills the
      // guard can never empty the pool, and pickSkill falls back to all of them if it ever does.
      fire(pickSkill(Math.random, chips.length, last === null ? [] : [last]));
      volleys.stop();
      if (live) volleys.start(between(Math.random, SHOTS.wait));
    };
    const onPress = () => press();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      if (!event.repeat) press();
    };
    // A press always produces a click right after it; 1.2 s covers even a long hold before the release.
    const onClick = () => {
      if (performance.now() - pressedAt > 1200) press();
    };

    button?.addEventListener("pointerdown", onPress);
    button?.addEventListener("keydown", onKey);
    button?.addEventListener("click", onClick);
    map.addEventListener("pointerover", onOver);
    map.addEventListener("pointerout", onOut);
    document.addEventListener("visibilitychange", update);
    reduce.addEventListener("change", update);
    constellation.addEventListener("change", onMedia);
    fine.addEventListener("change", onMedia);
    return () => {
      io.disconnect();
      volleys.stop();
      tour.stop();
      travel?.cancel();
      button?.removeEventListener("pointerdown", onPress);
      button?.removeEventListener("keydown", onKey);
      button?.removeEventListener("click", onClick);
      map.removeEventListener("pointerover", onOver);
      map.removeEventListener("pointerout", onOut);
      document.removeEventListener("visibilitychange", update);
      reduce.removeEventListener("change", update);
      constellation.removeEventListener("change", onMedia);
      fine.removeEventListener("change", onMedia);
    };
  }, []);

  return ref;
}

/* `brand` (brand.ts): "north" unless the /logo-test preview says "habit". */
export default function Skills({ brand = "north" }: { brand?: Brand }) {
  const map = useShots();

  return (
    <Reveal as="section" className="v2-skills" id={SECTION_IDS.skills}>
      <div className="v2-skills__inner">
        {/* reveal: tag, title, lead and button rise one after the other (--i 0 to 3). */}
        <SectionHeading
          reveal
          align="center"
          className="v2-skills__head"
          tag="The skills"
          tagVariant="blue"
          title={["Skills built for legal work.", "Tailored to your firm."]}
          lead="Specialized, governed skills for the work law firms actually run. Every firm we add makes the library deeper. More firms, more skills, everyone benefits."
        >
          <Button variant="dark" icon="arrow" href="/skills">
            Browse the library
          </Button>
        </SectionHeading>

        {/* Balls leave the star for a random skill, which bounces and lights up (useShots, skills.css). The twelve
            names are a real list; rays, rings, sonar, glass and balls are decoration. No JavaScript or reduced motion:
            a still constellation. */}
        <div ref={map} className="v2-skills__map">
          {/* Everything that orbits the star lives in one box, and that box sways a degree and a half either way about
              the star (skills.css, v2-skills-sway). A rotation of the whole group is the one movement that makes the
              constellation read as alive without moving a single chip relative to its neighbours: every distance
              between two chips is preserved exactly, so it can never close a gap. Below 900 px it is display:
              contents, so the wrapped list is laid out exactly as it was. */}
          <span className="v2-skills__sway">
            <span className="v2-skills__rays v2-reveal" aria-hidden="true">
              {SKILLS.map(({ label, place: p }, i) => (
                <span key={label} className="v2-skills__ray" style={place(p, i)} />
              ))}
            </span>
            <ul className="v2-skills__list">
              {SKILLS.map(({ label, place: p, icon }, i) => (
                // Two boxes on purpose: the <li> is where the chip SITS (its place on the map, its reveal, its bounce
                // when a ball lands), the <span> is what MOVES (the slow wander and the depth). Reveal and wander would
                // otherwise be two "animation" declarations fighting over one element, and one would silently win.
                <li key={label} className="v2-skills__chip v2-reveal" style={place(p, i)}>
                  <span className="v2-skills__chip-box">
                    <svg
                      className="v2-skills__icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {icon}
                    </svg>
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </span>
          {/* The star is a push button (Jérémy, 18/09: "the user can click it to launch skills — it should feel like a
              buzzer"). The hub itself is not hidden from screen readers any more: its decoration is, and the button
              inside it carries the one real label. The button does NOT sway with the constellation — its hit area
              stays exactly where the pointer expects it — and it does not press either: __core does, so the button's
              88 px target never shrinks under the finger. */}
          <span className="v2-skills__hub v2-reveal">
            <span className="v2-skills__core" aria-hidden="true">
              <span className="v2-skills__halo" />
              <span className="v2-skills__sonar" />
              <span className="v2-skills__sonar v2-skills__sonar--echo" />
              <span className="v2-skills__ring v2-skills__ring--3" />
              <span className="v2-skills__ring v2-skills__ring--2" />
              <span className="v2-skills__ring v2-skills__ring--1" />
              <NorthMark brand={brand} />
              <span className="v2-skills__flash" />
            </span>
            <button type="button" className="v2-skills__press">
              <span className="v2-skills__press-label">Send a signal to a skill</span>
            </button>
          </span>
          {/* The tour (useShots, skills-tour.ts): a simulated cursor and the tooltip it opens. Pure decoration, out of
              the sway so they are placed at measured coordinates; skills.css keeps both invisible unless the tour runs.
              The tooltip is filled by script, so the page's HTML carries no copy twice. */}
          <span className="v2-skills__cursor" aria-hidden="true">
            <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
              <path
                d="M1 1v15.2l3.9-3.7 2.7 6.1 2.6-1.1-2.7-6h5.3L1 1Z"
                fill="var(--v2-white)"
                stroke="var(--v2-ink)"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="v2-skills__tip" aria-hidden="true">
            <span className="v2-skills__tip-card">
              <span className="v2-skills__tip-name" />
              <span className="v2-skills__tip-blurb" />
              <span className="v2-skills__tip-meta" />
            </span>
          </span>
        </div>
      </div>
    </Reveal>
  );
}
